import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faImage, 
    faVideo, 
    faPodcast,
    faSpinner,
    faPlus,
    faImages,
    faTextHeight,
    faAlignLeft
} from '@fortawesome/free-solid-svg-icons';
import { galleryService } from '../../services/galleryService';
import PropTypes from 'prop-types';
import './NewGallery.css';

const NewGallery = ({ onClose, onSave, defaultGalleryType = 'image', existingGalleries = [] }) => {
    const [galleryData, setGalleryData] = useState({
        title: '',
        description: '',
        status: 'draft',
        galleryType: defaultGalleryType,
        isPublished: false
    });
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [titleWarning, setTitleWarning] = useState(null);

    // Update gallery type when default changes
    useEffect(() => {
        setGalleryData(prev => ({
            ...prev,
            galleryType: defaultGalleryType
        }));
    }, [defaultGalleryType]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setGalleryData(prev => ({
            ...prev,
            [name]: value
        }));

        // Check for duplicates in real-time when title changes
        if (name === 'title') {
            const duplicateError = checkForDuplicates(value);
            setTitleWarning(duplicateError);
            // Clear any previous submission errors when user starts typing
            if (error && error.includes('already exists')) {
                setError(null);
            }
        }
    };

    const handleGalleryTypeSelect = (type) => {
        setGalleryData(prev => ({
            ...prev,
            galleryType: type
        }));
    };

    // Check for duplicate gallery titles
    const checkForDuplicates = (title) => {
        if (!title.trim()) return null;
        
        const trimmedTitle = title.trim().toLowerCase();
        const duplicate = existingGalleries.find(gallery => 
            gallery.title?.toLowerCase().trim() === trimmedTitle
        );
        
        return duplicate ? `A gallery named "${title}" already exists. Please choose a different name.` : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        // Check for duplicates before submitting
        const duplicateError = checkForDuplicates(galleryData.title);
        if (duplicateError) {
            setError(duplicateError);
            setIsSubmitting(false);
            return;
        }

        try {
           
            const savedGallery = await galleryService.createGallery(galleryData);
            
            setShowSuccess(true);
            
            // Show success state briefly before closing
            setTimeout(() => {
                onSave(savedGallery);
            }, 1200);

        } catch (err) {
            console.error('Error creating gallery:', err);
            
            // Provide more specific error messages
            let userMessage = 'Failed to create gallery';
            if (err.message.includes('Invalid response')) {
                userMessage = 'Server error - please try again or contact support';
            } else if (err.message.includes('network') || err.message.includes('fetch')) {
                userMessage = 'Network error - please check your connection and try again';
            } else if (err.message) {
                userMessage = err.message;
            }
            
            setError(userMessage);
            setIsSubmitting(false);
        }
    };

    const getGalleryTypeConfig = () => {
        const configs = {
            image: {
                icon: faImage,
                label: 'Image Gallery',
                color: '#28a745',
                gradient: 'linear-gradient(135deg, #28a745, #20c997)'
            },
            video: {
                icon: faVideo,
                label: 'Video Gallery', 
                color: '#dc3545',
                gradient: 'linear-gradient(135deg, #dc3545, #fd7e14)'
            },
            podcast: {
                icon: faPodcast,
                label: 'Audio Gallery',
                color: '#ffc107',
                gradient: 'linear-gradient(135deg, #ffc107, #fd7e14)'
            }
        };
        return configs;
    };

    const configs = getGalleryTypeConfig();

    return (
        <>
            <div className="modern-modal-overlay" onClick={onClose}></div>
            <div className="modern-modal-container">
                <div className="modern-modal-content">
                    {/* Header */}
                    <div className="modern-modal-header">
                        <div className="header-content">
                            <div className="header-icon">
                                <FontAwesomeIcon icon={faImages} />
                            </div>
                            <div className="header-text">
                                <h2>Create New Gallery</h2>
                                <p>Set up a new gallery to organize your media content</p>
                            </div>
                        </div>
                        <button className="modern-close-btn" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="modern-modal-body">
                        {error && (
                            <div className="error-message">
                                <span>⚠️ {error}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="modern-form">
                            {/* Gallery Type Selector */}
                            <div className="form-section">
                                <label className="section-label">
                                    <FontAwesomeIcon icon={faImages} />
                                    Gallery Type
                                </label>
                                <div className="gallery-type-grid">
                                    {Object.entries(configs).map(([type, config]) => (
                                        <div
                                            key={type}
                                            className={`gallery-type-card ${galleryData.galleryType === type ? 'selected' : ''}`}
                                            onClick={() => handleGalleryTypeSelect(type)}
                                        >
                                            <div 
                                                className="type-icon"
                                                style={{ 
                                                    background: galleryData.galleryType === type ? config.gradient : '#f8f9fa',
                                                    color: galleryData.galleryType === type ? 'white' : config.color
                                                }}
                                            >
                                                <FontAwesomeIcon icon={config.icon} />
                                            </div>
                                            <span className="type-label">{config.label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Title Field */}
                            <div className="form-section">
                                <label className="section-label">
                                    <FontAwesomeIcon icon={faTextHeight} />
                                    Gallery Title
                                </label>
                                <div className="input-wrapper">
                                    <input
                                        type="text"
                                        name="title"
                                        value={galleryData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter a compelling gallery title..."
                                        className={`modern-input ${titleWarning ? 'has-warning' : ''}`}
                                        required
                                    />
                                    {titleWarning && (
                                        <div className="input-warning">
                                            ⚠️ {titleWarning}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Description Field */}
                            <div className="form-section">
                                <label className="section-label">
                                    <FontAwesomeIcon icon={faAlignLeft} />
                                    Description
                                </label>
                                <div className="input-wrapper">
                                    <textarea
                                        name="description"
                                        value={galleryData.description}
                                        onChange={handleInputChange}
                                        placeholder="Describe what this gallery will contain... (optional)"
                                        className="modern-textarea"
                                        rows={4}
                                    />
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="form-actions">
                                <button 
                                    type="button"
                                    className="btn-secondary"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className={`btn-primary ${showSuccess ? 'success' : ''}`}
                                    disabled={isSubmitting || !galleryData.title || titleWarning}
                                    style={{ 
                                        background: showSuccess 
                                            ? 'linear-gradient(135deg, #28a745, #20c997)' 
                                            : configs[galleryData.galleryType].gradient 
                                    }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            {showSuccess ? 'Created!' : 'Creating...'}
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faPlus} />
                                            Create Gallery
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

NewGallery.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    defaultGalleryType: PropTypes.oneOf(['image', 'video', 'podcast']),
    existingGalleries: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string,
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }))
};

export default NewGallery; 